import '@farmshare/ui-components/styles.scss'; // Import this first, as it includes the globals, so with matching specificity these are overriding other classes
import {
  Auth0Provider,
  Auth0ProviderOptions,
  WithAuthenticationRequiredOptions,
  withAuthenticationRequired,
} from '@auth0/auth0-react';
import {
  Error,
  FullscreenLoading,
  ModalProvider,
  MuiSwitchableThemeProvider,
  ToastrProvider,
} from '@farmshare/ui-components';
import * as Sentry from '@sentry/react';
import { environment } from 'environments/environment';
import { includes } from 'lodash';
import { ComponentType, StrictMode, lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import {
  RouterProvider,
  createBrowserRouter,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { RecoilRoot, useRecoilState } from 'recoil';
import { userState } from 'state';
import { getAuth0Config, isRunningInIframe } from 'utils/iframe-utils';

import { AuthApolloProvider } from 'components/auth-apollo-provider/auth-apollo-provider';

import { EnumUserRole } from 'lib/graphql';

import { App } from './app';
import logo from './assets/images/partners.svg';
import { RouterErrorBoundary } from './router-error-boundary';

Sentry.init({
  dsn: 'https://385f52358dc9796cfecdecab3e87c5ae@o4509940188512257.ingest.us.sentry.io/4509944746475520',
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  environment: import.meta.env.VITE_ENV,
  enabled: !!import.meta.env.VITE_ENV, // Is this running a built version? Or is it live reloading?
  integrations: [
    // Add browser profiling integration to the list of integrations
    Sentry.browserProfilingIntegration(),
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    // Add feedback integration so users can submit feedback
    Sentry.feedbackIntegration({
      colorScheme: 'system',
      isNameRequired: true,
      isEmailRequired: true,
      autoInject: false,
    }),
  ],
  // Let's sample 5% of sessions on prod and 20% on other environments, as we have way less traffic on staging and dev so let's sample more.
  tracesSampler: () => (import.meta.env.VITE_ENV === 'production' ? 0.05 : 0.2),
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/([a-z0-9]+[.])*farmshare[.]co/g,
  ],
  profilesSampleRate: 0.1, // This is relative to tracesSampleRate so this will profile 0.5% of total sessions on production and less everywhere else.
  replaysSessionSampleRate: 0.0001, // Let's sample 0.1% of sessions for replay
  replaysOnErrorSampleRate: 1, // When an error happens, let's replay 2% of sessions
});

// Lazy-loaded page components with Vite chunk names
const AdminPage = lazy(
  () => import(/* webpackChunkName: "admin" */ 'pages/admin'),
);
const AdminAssetPage = lazy(
  () => import(/* webpackChunkName: "admin-assets" */ 'pages/admin/assets'),
);
const AdminHangingWeightExportPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-hanging-export" */ 'pages/admin/hanging-export'
    ),
);
const AdminJobExportPage = lazy(
  () =>
    import(/* webpackChunkName: "admin-job-export" */ 'pages/admin/job-export'),
);
const AdminProcessorExportPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-processor-export" */ 'pages/admin/processor-export'
    ),
);
const AdminShipmentPage = lazy(
  () =>
    import(/* webpackChunkName: "admin-shipments" */ 'pages/admin/shipments'),
);
const AdminTenantPage = lazy(
  () => import(/* webpackChunkName: "admin-tenants" */ 'pages/admin/tenants'),
);
const AdminTenantDetailsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "admin-tenant-details" */ 'pages/admin/tenants/details'
    ),
);
const FAQPage = lazy(() => import(/* webpackChunkName: "faq" */ 'pages/faq'));
const ComparisonTool = lazy(
  () =>
    import(
      /* webpackChunkName: "comparison" */ 'components/comparison-tool/comparison-tool'
    ),
);
const HomePage = lazy(
  () => import(/* webpackChunkName: "home" */ 'pages/home'),
);
const IntegrationsPage = lazy(
  () => import(/* webpackChunkName: "integrations" */ 'pages/integrations'),
);
const LoginPage = lazy(
  () => import(/* webpackChunkName: "login" */ 'pages/login'),
);
const EditSplitType = lazy(() =>
  import(
    /* webpackChunkName: "edit-split-type" */ 'pages/processing-job/_views/edit-split-type'
  ).then((m) => ({ default: m.EditSplitType })),
);
const AnimalHead = lazy(() =>
  import(
    /* webpackChunkName: "animal-head" */ 'pages/processing-job/animal-head'
  ).then((m) => ({ default: m.AnimalHead })),
);
const AddAnimalHead = lazy(() =>
  import(
    /* webpackChunkName: "add-animal-head" */ 'pages/processing-job/animal-head-add'
  ).then((m) => ({ default: m.AddAnimalHead })),
);
const GenerateInvoice = lazy(() =>
  import(
    /* webpackChunkName: "generate-invoice" */ 'pages/processing-job/generate-invoice'
  ).then((m) => ({ default: m.GenerateInvoice })),
);
const ProcessingJob = lazy(() =>
  import(
    /* webpackChunkName: "processing-job" */ 'pages/processing-job/processing-job'
  ).then((m) => ({ default: m.ProcessingJob })),
);
const ProcessorAdminPage = lazy(
  () => import(/* webpackChunkName: "processor-admin" */ 'pages/processor'),
);
const ProcessorCustomerDetailsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "processor-customer-details" */ 'pages/processor/customers/_views/details'
    ),
);
const Waitlist = lazy(() =>
  import(
    /* webpackChunkName: "waitlist" */ 'pages/processor/scheduling-settings/_views/waitlist'
  ).then((m) => ({ default: m.Waitlist })),
);
const ChatPage = lazy(
  () => import(/* webpackChunkName: "chat" */ 'pages/producer/chat'),
);
const ProducerDashboard = lazy(
  () =>
    import(
      /* webpackChunkName: "producer-dashboard" */ 'pages/producer/dashboard'
    ),
);
const ProducerJobs = lazy(() =>
  import(/* webpackChunkName: "producer-jobs" */ 'pages/producer/jobs').then(
    (m) => ({ default: m.ProducerJobs }),
  ),
);
const ProfilePage = lazy(
  () => import(/* webpackChunkName: "profile" */ 'pages/profile'),
);
const PromosPage = lazy(
  () => import(/* webpackChunkName: "promos" */ 'pages/promos'),
);
const SchedulingPage = lazy(
  () => import(/* webpackChunkName: "scheduling" */ 'pages/scheduling'),
);
const SchedulingCreatePage = lazy(
  () =>
    import(
      /* webpackChunkName: "scheduling-create" */ 'pages/scheduling/create'
    ),
);
const SchedulingDetailsHome = lazy(
  () =>
    import(
      /* webpackChunkName: "scheduling-details" */ 'pages/scheduling/details'
    ),
);
const SchedulingEditCutsheets = lazy(
  () =>
    import(
      /* webpackChunkName: "scheduling-edit-cutsheets" */ 'pages/scheduling/edit'
    ),
);
const ProcessingPartnerDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "processing-partner-detail" */ 'pages/scheduling/processor'
    ),
);
const SignupPage = lazy(
  () => import(/* webpackChunkName: "signup" */ 'pages/signup'),
);
const WaitlistUnsubscribe = lazy(
  () =>
    import(
      /* webpackChunkName: "waitlist-unsubscribe" */ 'pages/waitlist-unsubscribe'
    ),
);
const ViewInvoicePage = lazy(() =>
  import(
    /* webpackChunkName: "view-invoice" */ 'pages/processor/invoices/view-invoice-page'
  ).then((m) => ({ default: m.ViewInvoicePage })),
);
const ExportsPage = lazy(
  () => import(/* webpackChunkName: "exports" */ 'pages/processor/exports'),
);

const NotificationsPage = lazy(() =>
  import(
    /* webpackChunkName: "notifications" */ 'pages/processor/notifications'
  ).then((m) => ({ default: m.NotificationsPage })),
);
const EditNotificationPage = lazy(() =>
  import(
    /* webpackChunkName: "edit-notification" */ 'pages/processor/notifications/edit-notification'
  ).then((m) => ({ default: m.EditNotificationPage })),
);

// create root
const root = createRoot(document.getElementById('root') as HTMLElement);

// Wrapper component for lazy-loaded pages with Suspense
const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<FullscreenLoading />}>{children}</Suspense>
);

// protected route component
const ProtectedRoute = (args: {
  component: ComponentType;
  options?: WithAuthenticationRequiredOptions;
  allowedUserRoles: EnumUserRole[];
  canAdminView?: boolean;
}) => {
  const [user] = useRecoilState(userState);

  const Component = withAuthenticationRequired(args.component, args.options);
  // if you pass in a list of allowedUserRoles and the user is not of those allowed roles.
  // Admins can access all things.
  if (
    user &&
    args.allowedUserRoles &&
    !includes(args.allowedUserRoles, user?.role) &&
    ((typeof args.canAdminView === 'undefined' &&
      user.role !== EnumUserRole.Admin) ||
      args.canAdminView === false)
  ) {
    return (
      <Error
        error={{ name: '404', message: 'Page not found.' }}
        logo={
          <img
            src={logo}
            height={100}
            alt="farmshare partners logo"
            className="mt-1 text-body p-3 p-md-0"
          />
        }
      />
    );
  }
  return (
    <LazyWrapper>
      <Component />
    </LazyWrapper>
  );
};

// auth provider
const Auth0ProviderWithRedirectCallback = ({
  children,
  ...props
}: Auth0ProviderOptions) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const connection = Boolean(searchParams.get('useEmail') === 'true')
    ? 'email'
    : Boolean(searchParams.get('usePhone') === 'true')
      ? 'sms'
      : undefined;

  // Get iframe-aware configuration
  const auth0Config = getAuth0Config();
  const isIframe = isRunningInIframe();

  return (
    <Auth0Provider
      onRedirectCallback={(appState) => {
        navigate(appState?.returnTo || window.location.pathname);
      }}
      cacheLocation={auth0Config.cacheLocation}
      useRefreshTokensFallback={auth0Config.useRefreshTokensFallback}
      useRefreshTokens={auth0Config.useRefreshTokens}
      // Additional iframe-specific settings
      skipRedirectCallback={isIframe}
      {...props}
      authorizationParams={{
        ...props.authorizationParams,
        scope: 'openid profile email phone offline_access',
        connection,
        // Add iframe-specific parameters
        ...(isIframe && { display: 'popup' }),
      }}
    >
      {children}
    </Auth0Provider>
  );
};

// create routing patterns
const router = createBrowserRouter([
  {
    path: '/promos',
    element: (
      <LazyWrapper>
        <PromosPage />
      </LazyWrapper>
    ),
  },
  {
    element: (
      <Auth0ProviderWithRedirectCallback
        domain={import.meta.env.VITE_AUTH0_DOMAIN || environment.authDomain}
        clientId={
          import.meta.env.VITE_AUTH0_CLIENT_ID || environment.authClientId
        }
        authorizationParams={{
          redirect_uri: window.location.origin,
          audience:
            import.meta.env.VITE_AUTH0_AUDIENCE || environment.authAudience,
        }}
      >
        <AuthApolloProvider>
          <ModalProvider>
            <ToastrProvider>
              <App />
            </ToastrProvider>
          </ModalProvider>
        </AuthApolloProvider>
      </Auth0ProviderWithRedirectCallback>
    ),
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <LazyWrapper>
            <HomePage />
          </LazyWrapper>
        ),
      },
      {
        path: '/login',
        element: (
          <LazyWrapper>
            <LoginPage />
          </LazyWrapper>
        ),
      },
      {
        path: '/signup',
        element: (
          <LazyWrapper>
            <SignupPage />
          </LazyWrapper>
        ),
      },
      {
        path: '/scheduling',
        element: (
          <LazyWrapper>
            <SchedulingPage />
          </LazyWrapper>
        ),
      },
      {
        path: '/faq',
        element: (
          <LazyWrapper>
            <FAQPage />
          </LazyWrapper>
        ),
      },
      {
        path: '/compare',
        element: (
          <LazyWrapper>
            <ComparisonTool />
          </LazyWrapper>
        ),
      },
      {
        path: '/scheduling/:slug',
        element: (
          <LazyWrapper>
            <ProcessingPartnerDetail />
          </LazyWrapper>
        ),
      },
      {
        path: '/scheduling/:slug/:animalType/:date',
        element: (
          <LazyWrapper>
            <SchedulingCreatePage />
          </LazyWrapper>
        ),
      },
      {
        path: '/scheduling/:schedulingId/details',
        element: (
          <LazyWrapper>
            <SchedulingDetailsHome />
          </LazyWrapper>
        ),
      },
      {
        path: '/scheduling/:schedulingId/cutsheets',
        element: (
          <ProtectedRoute
            component={SchedulingEditCutsheets}
            allowedUserRoles={[EnumUserRole.Basic, EnumUserRole.Vendor]}
          />
        ),
      },
      {
        path: '/admin',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Admin]}
            component={AdminPage}
          />
        ),
        children: [
          {
            path: '/admin/job-export',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminJobExportPage}
              />
            ),
          },
          {
            path: '/admin/hanging-export',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminHangingWeightExportPage}
              />
            ),
          },
          {
            path: '/admin/processor-export',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminProcessorExportPage}
              />
            ),
          },
          {
            path: '/admin/assets',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminAssetPage}
              />
            ),
          },
          {
            path: '/admin/shipments',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminShipmentPage}
              />
            ),
          },
          {
            path: '/admin/tenants',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminTenantPage}
              />
            ),
          },
          {
            path: '/admin/tenants/:id',
            element: (
              <ProtectedRoute
                allowedUserRoles={[EnumUserRole.Admin]}
                component={AdminTenantDetailsPage}
              />
            ),
          },
        ],
      },
      {
        path: '/integrations',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Vendor]}
            component={IntegrationsPage}
          />
        ),
      },
      {
        path: '/exports',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Vendor]}
            component={ExportsPage}
          />
        ),
      },
      {
        path: '/profile',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Basic, EnumUserRole.Vendor]}
            component={ProfilePage}
          />
        ),
      },
      {
        path: 'processor/notifications',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Vendor]}
            component={NotificationsPage}
          />
        ),
      },
      {
        path: 'processor/notifications/:id',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Vendor]}
            component={EditNotificationPage}
          />
        ),
      },
      {
        path: 'processor/settings/waitlist',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Vendor]}
            component={Waitlist}
          />
        ),
      },
      {
        path: '/waitlist/unsubscribe/:token',
        element: (
          <LazyWrapper>
            <WaitlistUnsubscribe />
          </LazyWrapper>
        ),
      },
      {
        path: '/processor/:tab?/:subTab?',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Vendor]}
            component={ProcessorAdminPage}
          />
        ),
      },
      {
        path: '/processor/customers/:id',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Vendor]}
            component={ProcessorCustomerDetailsPage}
          />
        ),
      },
      {
        path: '/processor/invoices/:invoiceId',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Vendor]}
            component={ViewInvoicePage}
          />
        ),
      },
      {
        path: 'processing-job/:jobId/generate-invoice',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Vendor]}
            component={GenerateInvoice}
          />
        ),
      },
      {
        path: 'processing-job/:jobId/:tab?',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Vendor, EnumUserRole.Basic]}
            component={ProcessingJob}
          />
        ),
      },
      {
        path: 'processing-job/:jobId/head/:animalHeadId?',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Vendor, EnumUserRole.Basic]}
            component={AnimalHead}
          />
        ),
      },
      {
        path: 'processing-job/:jobId/head/add',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Vendor]}
            component={AddAnimalHead}
          />
        ),
      },
      {
        path: 'processing-job/:jobId/head/:animalHeadId/edit-split-type',
        element: (
          <ProtectedRoute
            allowedUserRoles={[EnumUserRole.Vendor, EnumUserRole.Basic]}
            component={EditSplitType}
          />
        ),
      },
      {
        path: '/agenda',
        element: (
          <ProtectedRoute
            component={ProducerDashboard}
            allowedUserRoles={[EnumUserRole.Basic]}
            canAdminView={false}
          />
        ),
      },
      {
        path: '/jobs',
        element: (
          <ProtectedRoute
            component={ProducerJobs}
            allowedUserRoles={[EnumUserRole.Basic]}
            canAdminView={false}
          />
        ),
      },
      {
        path: ':userId?/chat/:id?',
        element: (
          <LazyWrapper>
            <ChatPage />
          </LazyWrapper>
        ),
      },
      // Fallback route for 404s
      {
        path: '*',
        element: (
          <Error
            error={{ name: '404', message: 'Page not found.' }}
            logo={
              <img
                src={logo}
                height={100}
                alt="farmshare partners logo"
                className="mt-1 text-body p-3 p-md-0"
              />
            }
          />
        ),
      },
      // {
      //   path: '/processor/floor/:tab?/:date?',
      //   element: (
      //     <ProtectedRoute
      //       allowedUserRoles={[EnumUserRole.Vendor]}
      //       component={ProcessingFloorPage}
      //     />
      //   ),
      // },
    ],
  },
]);

// Service worker will be initialized automatically via the enhanced hook

// render to root
root.render(
  <StrictMode>
    <RecoilRoot>
      <MuiSwitchableThemeProvider>
        <RouterProvider router={router} />
      </MuiSwitchableThemeProvider>
    </RecoilRoot>
  </StrictMode>,
);
